import { createContext, useContext, useMemo } from 'react';

/**
 * The device drawer runs in two modes:
 *  - `testing`  — My Testing Devices: every op behind staff-api's testing-account gate (no accountId).
 *  - `customer` — the customer Devices page: the same drawer (action bar, Settings, capture, live
 *                 troubleshoot). Every op that takes an `accountId` carries the page's account, which
 *                 switches staff-api to its customer gate (the device must be on that account). The
 *                 generic proxy ops (mdmCommand, toggleDeviceFeature, …) take none and are unchanged.
 */
export type DeviceDrawerMode = 'testing' | 'customer';

/**
 * Extra variables for a drawer op that accepts an `accountId`: `{ accountId }` in customer mode,
 * nothing otherwise. The key is omitted (not sent as undefined/null) in testing mode, so staff-api
 * keeps applying the testing gate and My Devices' requests stay byte-identical.
 */
export function customerReadVars(accountId: string | null | undefined): { accountId?: string } {
  return accountId ? { accountId } : {};
}

/**
 * The customer account a drawer is scoped to — provided by the drawer body in customer mode only, so
 * its dialogs (device info, command history, Wi-Fi, time zone, RS232, storage, execute, ops
 * schedules) pick it up without prop-drilling. Undefined in testing mode.
 */
export const DrawerAccountContext = createContext<string | undefined>(undefined);

/** `customerReadVars` for the enclosing drawer — `{}` in testing mode (and outside any drawer). */
export function useDrawerScopeVars(): { accountId?: string } {
  const accountId = useContext(DrawerAccountContext);
  // Stable per account, so it is safe in effect / callback deps.
  return useMemo(() => customerReadVars(accountId), [accountId]);
}
